import { Link } from "react-router-dom";
import { Building2, MonitorSmartphone, MoreHorizontal, Eye, Pencil, Trash2, ShieldCheck, ShieldOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tenant, TenantStatus, TENANT_STATUS_CONFIG } from "@/types/tenant";
import { format } from "date-fns";

interface TenantCardProps {
  tenant: Tenant;
  hasPendingDeletion: boolean;
  onRequestDeletion: (tenantId: string) => void;
}

export const TenantCard = ({ tenant, hasPendingDeletion, onRequestDeletion }: TenantCardProps) => {
  const statusConfig = TENANT_STATUS_CONFIG[tenant.tenant_status] || TENANT_STATUS_CONFIG.active;

  return (
    <Card className="relative group">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {tenant.logo_url ? (
              <img
                src={tenant.logo_url}
                alt={tenant.name}
                className="h-10 w-10 rounded-lg object-cover"
              />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="font-semibold text-sm truncate">{tenant.name}</h3>
              <p className="text-xs text-muted-foreground truncate">{tenant.email}</p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/admin/tenants/${tenant.id}`}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/admin/tenants/${tenant.id}/edit`}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Link>
              </DropdownMenuItem>
              {tenant.tenant_status !== 'deleted' && !hasPendingDeletion && (
                <DropdownMenuItem
                  onClick={() => onRequestDeletion(tenant.id)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Request Deletion
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Status & Type badges */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Badge variant="secondary" className={`${statusConfig.bgColor} ${statusConfig.color} border-0`}>
            {statusConfig.label}
          </Badge>
          <Badge variant="outline" className="capitalize">
            {tenant.tenant_type || 'customer'}
          </Badge>
          {hasPendingDeletion && (
            <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
              Deletion Pending
            </Badge>
          )}
        </div>

        {/* Details */}
        <div className="space-y-2.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground flex items-center gap-1.5">
              <MonitorSmartphone className="h-3.5 w-3.5" />
              Devices
            </span>
            <span className="font-medium">
              {tenant.device_count}
              {tenant.expected_device_count && tenant.expected_device_count > 0 && (
                <span className="text-muted-foreground font-normal"> / {tenant.expected_device_count}</span>
              )}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Parent Managed</span>
            {tenant.can_parent_manage !== false ? (
              <span className="flex items-center gap-1 text-emerald-600">
                <ShieldCheck className="h-3.5 w-3.5" />
                Yes
              </span>
            ) : (
              <span className="flex items-center gap-1 text-muted-foreground">
                <ShieldOff className="h-3.5 w-3.5" />
                No
              </span>
            )}
          </div>

          {tenant.industry && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Industry</span>
              <span className="capitalize">{tenant.industry}</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Created</span>
            <span>{tenant.created_at ? format(new Date(tenant.created_at), "MMM d, yyyy") : "-"}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
